import React from 'react';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { Building2, ChevronDown, Edit3, Loader2, Mail, Phone, Plus, User } from 'lucide-react';
import type { SelfServiceFormData } from '../../../types/selfService';
import { useAuth, type AdvertiserMembership } from '../auth/useAuth';
import { apiClient, ApiError } from '../../../lib/apiClient';
import PrimaryButton from '../shared/PrimaryButton';
import { FloatingField, FieldCard, StepHeader, accentVars, tintFromAccent } from './_shared';

// Single-line summary row that matches the wizard's existing data-display
// pattern (mirrors ReviewPayStep's internal Row + CampaignSummarySidebar).
// Lives in this file rather than `_shared.tsx` until a second step needs it.
const SummaryRow: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  secondary?: string | null;
  accentColor: string;
}> = ({ icon: Icon, label, value, secondary, accentColor }) => (
  <div className="flex items-center gap-3 px-5 py-3">
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
      style={{ backgroundColor: tintFromAccent(accentColor, 0.08), color: accentColor }}
    >
      <Icon className="w-3.5 h-3.5" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">{label}</div>
      <div className="text-[13px] text-slate-900 font-medium tracking-tight truncate">{value || '—'}</div>
      {secondary && (
        <div className="text-[11px] text-slate-500 truncate mt-0.5">{secondary}</div>
      )}
    </div>
  </div>
);

// Multi-seat variant of the Business SummaryRow used only in the read-only
// summary when the user has >=2 memberships. The select swaps the static value
// line and writes to the same selectedAdvertiserId store that HeaderAuth uses,
// so the wizard switcher and the header switcher stay in sync.
const BusinessSummaryDropdown: React.FC<{
  memberships: AdvertiserMembership[];
  selectedId: string;
  websiteFallback: string;
  onChange: (id: string) => void;
  accentColor: string;
}> = ({ memberships, selectedId, websiteFallback, onChange, accentColor }) => {
  const selected = memberships.find((m) => m.advertiserId === selectedId);
  const secondary = selected?.businessWebsite || websiteFallback;
  return (
    <div className="flex items-center gap-3 px-5 py-3">
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: tintFromAccent(accentColor, 0.08), color: accentColor }}
      >
        <Building2 className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">Business</div>
        <div className="relative inline-flex max-w-full items-center">
          <select
            value={selectedId}
            onChange={(e) => onChange(e.target.value)}
            className="appearance-none bg-transparent text-[13px] text-slate-900 font-medium tracking-tight pr-5 max-w-full truncate cursor-pointer focus:outline-none"
          >
            {memberships.map((m) => (
              <option key={m.advertiserId} value={m.advertiserId}>
                {m.advertiserName}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-0 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        </div>
        {secondary && (
          <div className="text-[11px] text-slate-500 truncate mt-0.5">{secondary}</div>
        )}
      </div>
    </div>
  );
};

function formatUSPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  const a = digits.slice(0, 3);
  const b = digits.slice(3, 6);
  const c = digits.slice(6, 10);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${a}`;
  if (digits.length < 7) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
}

interface BusinessInfoStepProps {
  accentColor?: string;
  primaryColor?: string;
  /**
   * Station slug. Required so the step's own Save handler for the "Add new
   * business" path can fire POST /memberships/ensure-self without the wizard
   * having to wrap it (the seat redesign keeps the call site co-located with
   * the form that produced the values).
   */
  slug: string;
  /**
   * Fires when the user enters or exits "Advertise with Another Business"
   * mode. Kept for analytics + wizard awareness; the gating signal that
   * actually disables Continue is `onEditingChange` (which now fires for
   * both Edit-details and Add-new-business modes — they share the same
   * commit-then-continue contract).
   */
  onCreatingNewChange?: (creatingNew: boolean) => void;
  /**
   * Fires when the user enters or exits a seat-edit panel — either Edit
   * details OR Add new business. The wizard reads this to gate Continue
   * while seat-field changes are uncommitted (CTV-562 + follow-up):
   * Continue stays disabled until Save or Discard commits/reverts.
   */
  onEditingChange?: (editing: boolean) => void;
}

// Snapshot taken when the user enters edit mode. Discard restores from this;
// Save diffs the current form values against it before deciding which
// endpoints to fire. Five fields because contactEmail re-renders the panel
// (teammate switch) but isn't itself written to anything on Save.
type EditSnapshot = {
  businessName: string;
  businessWebsite: string;
  contactEmail: string;
  contactName: string;
  contactPhone: string;
};

const BusinessInfoStep: React.FC<BusinessInfoStepProps> = ({
  accentColor = '#3b82f6',
  primaryColor,
  slug,
  onCreatingNewChange,
  onEditingChange,
}) => {
  const { register, control, setValue, getValues, trigger, formState: { errors } } = useFormContext<SelfServiceFormData>();
  const { user, selectedAdvertiser, advertiserMemberships, setSelectedAdvertiserId, refreshMemberships, refreshProfile } = useAuth();

  // Form-side watchers: the read-only summary mirrors the form, NOT the
  // user's profile — that's the only way "Run this Campaign Again" can show
  // the copied submission's contact info while the current user (the new
  // submitter) is someone else.
  const watchedBusinessName = useWatch({ control, name: 'businessName' });
  const watchedBusinessWebsite = useWatch({ control, name: 'businessWebsite' });
  const watchedContactEmail = useWatch({ control, name: 'contactEmail' });
  const watchedContactName = useWatch({ control, name: 'contactName' });
  const watchedContactPhone = useWatch({ control, name: 'contactPhone' });

  // Seat redesign: when the user already has a seat selected, show the
  // read-only summary by default. They can click Edit to mutate the seat's
  // business fields + their own profile, or click "Advertise with Another
  // Business" to spin up a new seat for a different client.
  const hasSeat = !!selectedAdvertiser;
  const [editing, setEditing] = React.useState(!hasSeat);
  // "Advertise with Another Business" mode: same form, but on save we fire
  // ensure-self instead of just persisting. The wizard's Continue handler
  // owns the ensure-self call now — we just notify it via the prop.
  const [creatingNew, setCreatingNew] = React.useState(false);

  // Notify the wizard whenever creatingNew flips so its handleNext knows
  // whether to fire ensure-self before advancing past step 1.
  React.useEffect(() => {
    onCreatingNewChange?.(creatingNew);
  }, [creatingNew, onCreatingNewChange]);

  // CTV-562 (+ follow-up): notify the wizard whenever the user has an open
  // seat-edit panel — either Edit details OR Add new business. The wizard
  // gates Continue on this signal so the user can't advance past Step 1
  // with uncommitted seat changes.
  //
  // The "no seat" auto-edit path (brand-new buyer who hasn't created a seat
  // yet) is intentionally NOT a wizard-gated edit — Continue still flows
  // through the email-verify ensure-self path for that case.
  React.useEffect(() => {
    onEditingChange?.(editing && hasSeat);
  }, [editing, hasSeat, onEditingChange]);

  // CTV-562: edit-mode snapshot. Captured at the moment editing flips true
  // (from `methods.getValues()` so it doesn't lag prop changes); cleared on
  // exit. Discard restores from this; Save diffs against it.
  const snapshotRef = React.useRef<EditSnapshot | null>(null);
  React.useEffect(() => {
    if (editing && !creatingNew && hasSeat && snapshotRef.current === null) {
      const v = getValues();
      snapshotRef.current = {
        businessName: v.businessName ?? '',
        businessWebsite: v.businessWebsite ?? '',
        contactEmail: v.contactEmail ?? '',
        contactName: v.contactName ?? '',
        contactPhone: v.contactPhone ?? '',
      };
    }
    if (!editing) {
      snapshotRef.current = null;
    }
  }, [editing, creatingNew, hasSeat, getValues]);

  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // Keep editing mode in sync with seat availability — when the user signs
  // in mid-wizard and a seat materializes, collapse back to the read-only
  // summary unless they're explicitly creating a new seat.
  React.useEffect(() => {
    if (hasSeat && !creatingNew) setEditing(false);
    if (!hasSeat) setEditing(true);
  }, [hasSeat, creatingNew]);

  // If the user clicks Continue from the read-only summary and any step-1
  // field fails validation (the wizard calls methods.trigger on those
  // fields), the errors are stored on react-hook-form's `errors` object but
  // there are no inputs rendered to display them. Auto-flip into edit mode
  // so the inline FloatingField errors become visible and the user has a
  // path to fix the offending value.
  const hasStep1Errors =
    !!errors.businessName ||
    !!errors.businessWebsite ||
    !!errors.contactEmail ||
    !!errors.contactName ||
    !!errors.contactPhone;
  React.useEffect(() => {
    if (hasStep1Errors && !editing) setEditing(true);
  }, [hasStep1Errors, editing]);

  // Contact email is always pinned to the signed-in user's email — when they
  // enter edit mode on an existing seat we snap contactEmail back to
  // user.email so any stale value copied in from a rerun-from submission
  // (which may point at a different teammate or a since-revoked user) is
  // corrected before the user hits Save.
  React.useEffect(() => {
    if (!editing || creatingNew || !hasSeat) return;
    if (!user?.email) return;
    const current = (getValues('contactEmail') ?? '').trim().toLowerCase();
    if (current !== user.email.toLowerCase()) {
      setValue('contactEmail', user.email, { shouldDirty: false, shouldValidate: false });
    }
  }, [editing, creatingNew, hasSeat, user?.email, getValues, setValue]);

  const startCreateNew = () => {
    setCreatingNew(true);
    setEditing(true);
    // Blank out the business fields; force contact email to the current
    // user's email (new business = current user is the contact). Keep
    // contactName / contactPhone seeded from the user's profile so they
    // don't have to retype, but they're editable.
    setValue('businessName', '', { shouldDirty: true });
    setValue('businessWebsite', '', { shouldDirty: true });
    if (user?.email) {
      setValue('contactEmail', user.email, { shouldDirty: true });
    }
    if (user?.contactName) {
      setValue('contactName', user.contactName, { shouldDirty: true });
    }
    if (user?.contactPhone) {
      setValue('contactPhone', user.contactPhone, { shouldDirty: true });
    }
  };

  const cancelCreateNew = () => {
    setCreatingNew(false);
    setEditing(false);
    // Restore the selected seat's values into the form so the read-only
    // summary renders correctly.
    if (selectedAdvertiser) {
      setValue('businessName', selectedAdvertiser.advertiserName, { shouldDirty: false });
      setValue('businessWebsite', selectedAdvertiser.businessWebsite ?? '', { shouldDirty: false });
    }
  };

  // contactEmail is pinned to the signed-in user's email (the
  // teammate-switcher was removed), so the profile PUT gate is just "is the
  // user signed in" — contactName / contactPhone always write to their own
  // selfservice_advertiser_profiles row.
  const canWriteOwnProfile = !!user?.email;

  const handleDiscard = () => {
    const snap = snapshotRef.current;
    setSaveError(null);
    if (snap) {
      const opts = { shouldDirty: false, shouldValidate: false };
      setValue('businessName', snap.businessName, opts);
      setValue('businessWebsite', snap.businessWebsite, opts);
      setValue('contactEmail', snap.contactEmail, opts);
      setValue('contactName', snap.contactName, opts);
      setValue('contactPhone', snap.contactPhone, opts);
    }
    snapshotRef.current = null;
    setEditing(false);
  };

  const handleSave = async () => {
    if (!selectedAdvertiser) return;
    // CTV-562 follow-up: run the same zod refinements Continue runs so the
    // user can't persist invalid values via Save. types/selfService.ts
    // enforces a website-shape regex (`host.tld` minimum, http(s)://
    // optional) and a phone-mask regex (exactly `(XXX) XXX-XXXX`). On
    // failure the FloatingField error chips render inline via the existing
    // `errors.<field>?.message` wiring — no extra UX glue needed.
    const valid = await trigger([
      'businessName',
      'businessWebsite',
      'contactEmail',
      'contactName',
      'contactPhone',
    ]);
    if (!valid) {
      setSaveError(null);
      return;
    }
    const snap = snapshotRef.current;
    if (!snap) {
      // Defensive: no snapshot means we never captured edit-mode entry, so
      // there's nothing to diff. Treat as a clean exit.
      setEditing(false);
      return;
    }
    const v = getValues();
    const seatPatch: { name?: string; businessWebsite?: string | null } = {};
    const nextName = (v.businessName ?? '').trim();
    const snapName = snap.businessName.trim();
    if (nextName !== snapName) seatPatch.name = nextName;
    const nextWebsite = (v.businessWebsite ?? '').trim();
    const snapWebsite = snap.businessWebsite.trim();
    if (nextWebsite !== snapWebsite) {
      seatPatch.businessWebsite = nextWebsite.length > 0 ? nextWebsite : null;
    }

    // PUT /profiles/me is a whole-resource upsert — missing keys become
    // NULL (see profiles.service.ts). So we send the full (name, phone)
    // pair on every fire, not just the diff: editing the name without the
    // phone otherwise wipes the saved phone column. `profileDirty` gates
    // whether we fire at all so an unchanged profile is still a no-op.
    let profileDirty = false;
    let profileBody: { contactName: string; contactPhone: string } | null = null;
    if (canWriteOwnProfile) {
      const nextContactName = (v.contactName ?? '').trim();
      const snapContactName = snap.contactName.trim();
      // Phone formatting mask would cause spurious diffs ("(212) 555-1234" vs
      // "2125551234"); compare on digits-only so re-saving an unchanged
      // number is a no-op.
      const nextDigits = (v.contactPhone ?? '').replace(/\D/g, '');
      const snapDigits = snap.contactPhone.replace(/\D/g, '');
      profileDirty = nextContactName !== snapContactName || nextDigits !== snapDigits;
      if (profileDirty) {
        profileBody = {
          contactName: nextContactName,
          contactPhone: v.contactPhone ?? '',
        };
      }
    }

    const noChanges = Object.keys(seatPatch).length === 0 && !profileDirty;
    if (noChanges) {
      snapshotRef.current = null;
      setSaveError(null);
      setEditing(false);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      if (Object.keys(seatPatch).length > 0) {
        await apiClient.patch(`/advertisers/${selectedAdvertiser.advertiserId}`, seatPatch);
        // Pulls the new business_website into useAuth so the dropdown variant
        // summary (which reads selected.businessWebsite from /memberships/mine)
        // re-renders against the freshly saved seat value. Without this the
        // bug being closed (CTV-562) reappears the next time the user enters
        // edit mode.
        await refreshMemberships();
      }
      if (profileBody) {
        await apiClient.put('/profiles/me', profileBody);
        // The wizard body is keyed by `${advertiserId}:${userId}` (see
        // SelfServiceWizard.tsx) — switching seats unmounts + remounts and
        // re-seeds the form from `user.contactName / user.contactPhone` on
        // useAuth. Without this refresh the auth cache stays stale, so a
        // seat-switch-and-back resurrects the OLD contact values over the
        // just-saved ones. The form's live watch survives the round-trip
        // within a single mount, but the remount is what bites us here.
        await refreshProfile();
      }
      snapshotRef.current = null;
      setEditing(false);
    } catch (e) {
      setSaveError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to save changes.',
      );
    } finally {
      setSaving(false);
    }
  };

  // CTV-562 follow-up: Save handler for the "Add new business" path. Same
  // commit-then-continue contract as handleSave (validate → write → exit),
  // but the write is POST /memberships/ensure-self (creates the seat) plus
  // PUT /profiles/me (persists the contact to the user's account — same
  // semantics as Edit details, which the prior plan deferred). Continue
  // stays gated until this completes successfully because onEditingChange
  // now fires for both Edit and Add-new-business modes.
  const handleSaveNewBusiness = async () => {
    const valid = await trigger([
      'businessName',
      'businessWebsite',
      'contactEmail',
      'contactName',
      'contactPhone',
    ]);
    if (!valid) {
      setSaveError(null);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const v = getValues();
      const ensureBody = {
        slug,
        businessName: (v.businessName ?? '').trim(),
        businessWebsite: (v.businessWebsite ?? '').trim() || null,
      };
      const resp = await apiClient.post<{ advertiserId: string; membershipId: string }>(
        '/memberships/ensure-self',
        ensureBody,
      );
      // Pull the new membership into useAuth and switch the active seat
      // before clearing creatingNew — the read-only summary mounts against
      // selectedAdvertiser, so this ordering avoids a one-render flash of
      // the previous seat's data.
      await refreshMemberships();
      setSelectedAdvertiserId(resp.advertiserId);

      // Persist the contact pair to the user's profile row. Always sends
      // both fields (whole-resource PUT semantics — see profiles.service.ts
      // and the prior CTV-562 fix). For creatingNew the contactEmail is
      // pinned to the signed-in user, so this always targets their own row.
      await apiClient.put('/profiles/me', {
        contactName: (v.contactName ?? '').trim(),
        contactPhone: v.contactPhone ?? '',
      });
      await refreshProfile();

      setCreatingNew(false);
      setEditing(false);
    } catch (e) {
      setSaveError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to add the new business.',
      );
    } finally {
      setSaving(false);
    }
  };

  // Read-only summary view (default when the user has a selected seat).
  if (hasSeat && !editing) {
    return (
      <div className="-m-4 sm:-m-6 md:-m-8" style={accentVars(accentColor, primaryColor)}>
        <div className="px-6 sm:px-10 md:px-14 pt-6 pb-6">
          <StepHeader step={1} label="Your business" accentColor={accentColor} />
        </div>

        <div className="px-6 sm:px-10 md:px-14 pb-10">
          <FieldCard accentColor={accentColor}>
            {advertiserMemberships && advertiserMemberships.length >= 2 ? (
              <BusinessSummaryDropdown
                memberships={advertiserMemberships}
                selectedId={selectedAdvertiser!.advertiserId}
                websiteFallback={watchedBusinessWebsite || selectedAdvertiser!.businessWebsite || ''}
                onChange={setSelectedAdvertiserId}
                accentColor={accentColor}
              />
            ) : (
              <SummaryRow
                icon={Building2}
                label="Business"
                value={watchedBusinessName || selectedAdvertiser!.advertiserName}
                secondary={watchedBusinessWebsite || selectedAdvertiser!.businessWebsite}
                accentColor={accentColor}
              />
            )}
            <SummaryRow
              icon={Mail}
              label="Contact email"
              value={watchedContactEmail || ''}
              accentColor={accentColor}
            />
            <SummaryRow
              icon={User}
              label="Contact name"
              value={watchedContactName || ''}
              accentColor={accentColor}
            />
            <SummaryRow
              icon={Phone}
              label="Phone"
              value={watchedContactPhone || ''}
              accentColor={accentColor}
            />
          </FieldCard>

          {/* Action pills — same visual vocabulary as MyCampaignsPage's per-
              submission actions (campaignActions.tsx). Edit gets the neutral
              slate pill (Cancel-style); Advertise with Another Business gets
              the accent-tinted "forward" pill (Run-this-ad-again style). */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-all"
            >
              <Edit3 className="w-3.5 h-3.5" />
              Edit details
            </button>
            <button
              type="button"
              onClick={startCreateNew}
              className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border hover:brightness-105 transition-all"
              style={{
                color: accentColor,
                borderColor: `${accentColor}40`,
                backgroundColor: `${accentColor}0f`,
              }}
            >
              <Plus className="w-3.5 h-3.5" />
              Add New Business
            </button>
          </div>

        </div>
      </div>
    );
  }

  // Editable mode: either the user has no seat yet (brand-new buyer) OR they
  // clicked Edit / Advertise with Another Business.
  return (
    <div className="-m-4 sm:-m-6 md:-m-8" style={accentVars(accentColor, primaryColor)}>
      <div className="px-6 sm:px-10 md:px-14 pt-6 pb-6">
        <StepHeader step={1} label="Your business" accentColor={accentColor} />
      </div>

      <div className="px-6 sm:px-10 md:px-14 pb-10">
        <FieldCard accentColor={accentColor}>

          <FloatingField id="businessName" label="Business name" required error={errors.businessName?.message}>
            {(p) => <input {...p} {...register('businessName')} />}
          </FloatingField>

          <FloatingField id="businessWebsite" label="Business website" required error={errors.businessWebsite?.message}>
            {(p) => <input {...p} {...register('businessWebsite')} />}
          </FloatingField>

          <FloatingField id="contactName" label="Contact name">
            {(p) => <input {...p} {...register('contactName')} />}
          </FloatingField>

          {/* Contact email is always the signed-in user's email — the
              wizard no longer supports attributing a campaign to a
              teammate. Anon submissions (no auth) still get a free-form
              input so a brand-new buyer can put in their email. */}
          {user?.email ? (
            <div className="flex items-center gap-3 px-5 py-3">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: tintFromAccent(accentColor, 0.08), color: accentColor }}
              >
                <Mail className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
                  Contact email
                </div>
                <div className="text-[13px] text-slate-900 font-medium tracking-tight truncate">
                  {user.email}
                </div>
              </div>
            </div>
          ) : (
            <FloatingField id="contactEmail" label="Contact email" required error={errors.contactEmail?.message}>
              {(p) => <input {...p} type="email" inputMode="email" autoComplete="email" {...register('contactEmail')} />}
            </FloatingField>
          )}

          <FloatingField id="contactPhone" label="Phone number" error={errors.contactPhone?.message}>
            {(p) => (
              <Controller
                name="contactPhone"
                control={control}
                render={({ field }) => (
                  <input
                    {...p}
                    {...field}
                    type="tel"
                    inputMode="tel"
                    value={formatUSPhone(field.value || '')}
                    onChange={(e) => field.onChange(formatUSPhone(e.target.value))}
                  />
                )}
              />
            )}
          </FloatingField>

        </FieldCard>

        {/* CTV-562: edit-mode action row.
            - !creatingNew + editing + hasSeat → Save (primary) + Discard
              (text). Save persists seat + own-profile diffs via PATCH
              /advertisers/:id and PUT /profiles/me, then exits edit mode.
              Discard restores the snapshot taken on entry.
            - creatingNew + editing + hasSeat → single Discard text pill
              (relabel of the previous "Cancel"), keeping the ensure-self-on-
              Continue model untouched. */}
        {saveError && (
          <div className="mt-3 text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded-md p-3">
            {saveError}
          </div>
        )}
        {hasSeat && editing && !creatingNew && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <PrimaryButton
              type="button"
              onClick={handleSave}
              disabled={saving}
              accentColor={accentColor}
              primaryColor={primaryColor}
              size="sm"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save changes
            </PrimaryButton>
            <button
              type="button"
              onClick={handleDiscard}
              disabled={saving}
              className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-all disabled:opacity-50"
            >
              Discard
            </button>
          </div>
        )}
        {hasSeat && creatingNew && editing && (
          // CTV-562 follow-up: Add-new-business now mirrors Edit-details's
          // commit-then-continue model. Save creates the seat via ensure-
          // self AND persists contact name/phone to the user's profile.
          // Discard reverts to the previously-selected seat (cancelCreateNew
          // already restores its display values into the form).
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <PrimaryButton
              type="button"
              onClick={handleSaveNewBusiness}
              disabled={saving}
              accentColor={accentColor}
              primaryColor={primaryColor}
              size="sm"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save new business
            </PrimaryButton>
            <button
              type="button"
              onClick={cancelCreateNew}
              disabled={saving}
              className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-all disabled:opacity-50"
            >
              Discard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessInfoStep;
